"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.leaveTimelineSchema = exports.leaveTimelineRowSchema = exports.leaveBandSchema = exports.myLoggingIssuesSchema = exports.loggingViewSchema = exports.loggingColumnSchema = exports.loggingRowSchema = exports.loggingProjectRowSchema = exports.loggingCellSchema = void 0;
const zod_1 = require("zod");
const working_day_1 = require("./working-day");
const leave_request_1 = require("./leave-request");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The two read-only dashboards — C49, Phase 3.                              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * **Logolás** answers *"is everybody logging their time?"*; **Szabadságok** answers *"who is away,
 * and when?"*. Both observe and decide nothing.
 *
 * ⚠️⚠️ **READ-ONLY IS THE WHOLE DESIGN, AND IT LEAVES ONE QUESTION OPEN ON PURPOSE.** Neither
 * dashboard closes a period: our own Agent preamble names timesheet approval as absent and the
 * mockup does not draw it either, so `PHASE-3.md` carries it as a question rather than a gap.
 * Nothing here forecloses it — closing a period would be a new table and a verb, not a rewrite of
 * these reads.
 *
 * ⚠️ **Every figure is C44's.** The grid's cells are the kernel's eight verdicts; the timeline's
 * bands are C47's approved and pending requests. A dashboard that recomputed either would be a
 * second answer to a question that already has one.
 */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** One person's row on the Logolás grid. */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ LOGOLÁS — A SPREADSHEET, NOT A STRIP OF COLOURED SQUARES.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The first version drew one cell per DAY with a verdict colour and a variance at the end, which
 * carried no figures at all: the product owner's report was *"no numbers, no detailed view
 * breakdown, no search, no toggle between monthly and daily"*. The mockup's `dashlog` is a grid of
 * **spans** — a week per column by default, a day per column on the other toggle — and every person
 * is **three lines** deep:
 *
 * | Line       | What it holds                                                          |
 * | ---------- | ---------------------------------------------------------------------- |
 * | Munkaidő   | what the schedule and the calendar expected of that span               |
 * | Rögzített  | what they logged, tinted by the verdict against the line above         |
 * | Szabadság  | approved leave inside the span, in hours                                |
 *
 * ⚠️ **And each person opens** into their own projects, one row each, so *"where did the 21:30
 * go"* is answered on the same screen rather than in a report.
 */
exports.loggingCellSchema = zod_1.z.object({
    /**
     * HOURS, or `null` where there is nothing to say — a flex day's plan, a span with no leave.
     * ⚠️ 3 Nov 2026 · a number, no longer `H:MM` text: the reader's unit (hours or man-days,
     * `decisions.md` DU-1) is the screen's to apply, not the server's.
     */
    value: zod_1.z.number().nullable(),
    /**
     * The kernel's verdict for the SPAN, on the `Rögzített` line only.
     *
     * ⚠️ `null` on the other two lines and on a span nobody could have worked: a tint is a claim
     * about a difference, and a plan has nothing to differ from.
     */
    verdict: zod_1.z.enum(working_day_1.DAY_VERDICTS).nullable(),
});
exports.loggingProjectRowSchema = zod_1.z.object({
    /**
     * ⚠️⚠️ **`unknown` — 28 Oct 2026: *Ismeretlen sortétel*.** Hours no line item holds (the
     * reports' *Ismeretlen logolások*: no line item on the worklog, no recurring contract on its
     * task) are MOVED OUT of their project's line into this one, pinned first. A task with no project
     * is always one of these (`0164`), so there is no *Projekt nélkül* line any more.
     */
    kind: zod_1.z.enum(['unknown', 'project']),
    /** `null` on the `unknown` line. */
    projectId: zod_1.z.string().nullable(),
    name: zod_1.z.string(),
    color: zod_1.z.string().nullable(),
    /** The project's manager, shown under the name. `null` for the no-project row. */
    pmName: zod_1.z.string().nullable(),
    /** ⚠️ Their picture, so the expanded row shows a face rather than a second line of grey text. */
    pmAvatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from**, when there is no picture
     * above. The normalised email, produced by `wireAvatarSeed` — nullable exactly like the URL
     * beside it, because a row with nobody in it has no face either.
     */
    pmAvatarSeed: zod_1.z.string().nullable(),
    /** ⚠️ For the identity colour behind their initials, which is derived from the id. */
    pmMemberId: zod_1.z.string().nullable(),
    /** HOURS per column — a number since 3 Nov 2026 (DU-1); the screen formats it. */
    hours: zod_1.z.array(zod_1.z.number()),
});
exports.loggingRowSchema = zod_1.z.object({
    memberId: zod_1.z.string(),
    memberName: zod_1.z.string(),
    /**
     * ⚠️⚠️ **THEIR PICTURE — 15 Sep.** Both dashboards drew a disc of initials, and the leave
     * timeline drew the same disc in the same grey for everybody until the identity colour landed.
     * A face is how somebody finds their own row in twenty; `person.avatarUrl` is the same path the
     * people list serves, and the browser's avatar cache fetches each one once for the whole app.
     */
    avatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from**, when there is no picture
     * above. The normalised email, produced by `wireAvatarSeed` — nullable exactly like the URL
     * beside it, because a row with nobody in it has no face either.
     */
    avatarSeed: zod_1.z.string().nullable(),
    /** Their job title, under the name — `drole` in the mockup. */
    role: zod_1.z.string().nullable(),
    scheduleType: zod_1.z.enum(['fix', 'flex']).nullable(),
    /** The weekly frame, for a `flex` person only — P3-7. */
    weeklyFrame: zod_1.z.string().nullable(),
    /**
     * ⚠️ **How many days in the window fell short**, which is what the warning triangle beside the
     * name means. A row with none is quiet.
     */
    shortDays: zod_1.z.number().int().nonnegative(),
    /**
     * 28 Oct 2026 · the window's hours on an unknown line item, in HOURS (a number since 3 Nov 2026,
     * DU-1) — the red mark beside the name. `null` when there are none.
     */
    unknownHours: zod_1.z.number().nullable(),
    planned: zod_1.z.array(exports.loggingCellSchema),
    logged: zod_1.z.array(exports.loggingCellSchema),
    leave: zod_1.z.array(exports.loggingCellSchema),
    projects: zod_1.z.array(exports.loggingProjectRowSchema),
    varianceHours: zod_1.z.string(),
});
/** One column of the grid — a week, or a day. */
exports.loggingColumnSchema = zod_1.z.object({
    key: zod_1.z.string(),
    from: wireDateSchema,
    to: wireDateSchema,
    /** The heading's first line: a date range in week mode, a date in day mode. */
    top: zod_1.z.string(),
    /** Its second line: the ISO week number, or the weekday. */
    sub: zod_1.z.string(),
    /** Whether today falls inside it — the mockup tints that column's header. */
    current: zod_1.z.boolean(),
});
exports.loggingViewSchema = zod_1.z.object({
    from: wireDateSchema,
    to: wireDateSchema,
    mode: zod_1.z.enum(['week', 'day']),
    columns: zod_1.z.array(exports.loggingColumnSchema),
    rows: zod_1.z.array(exports.loggingRowSchema),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 28 Oct 2026 · MY OWN LOGGING ISSUES — the sidebar's *Idő* entry.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The two marks the Logolás grid puts beside a name, for the person signed in and the last thirty
 * days: hours on an unknown line item (a warning) and short days (a note). Anybody may read their
 * own — `dashboard.logging` gates other people's rows, not this.
 */
exports.myLoggingIssuesSchema = zod_1.z.object({
    from: wireDateSchema,
    to: wireDateSchema,
    /** HOURS, or `null` when there are none — `loggingRowSchema.unknownHours`. */
    unknownHours: zod_1.z.number().nullable(),
    /** Days BEFORE today that fell short; the day in progress is never one. */
    shortDays: zod_1.z.number().int().nonnegative(),
});
/** One leave band on the Szabadságok timeline. */
exports.leaveBandSchema = zod_1.z.object({
    requestId: zod_1.z.string(),
    leaveTypeName: zod_1.z.string(),
    status: zod_1.z.enum(leave_request_1.LEAVE_REQUEST_STATUSES),
    fromDate: wireDateSchema,
    toDate: wireDateSchema,
});
exports.leaveTimelineRowSchema = zod_1.z.object({
    memberId: zod_1.z.string(),
    memberName: zod_1.z.string(),
    /** ⚠️ Their picture, as on the logging grid — see `loggingRowSchema.avatarUrl`. */
    avatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from**, when there is no picture
     * above. The normalised email, produced by `wireAvatarSeed` — nullable exactly like the URL
     * beside it, because a row with nobody in it has no face either.
     */
    avatarSeed: zod_1.z.string().nullable(),
    /** Their job title, under the name — the same `drole` the logging grid shows. */
    role: zod_1.z.string().nullable(),
    bands: zod_1.z.array(exports.leaveBandSchema),
    /** ⚠️ The working days this person is away inside the window — C44's count, not the span. */
    daysInWindow: zod_1.z.number().int().nonnegative(),
});
exports.leaveTimelineSchema = zod_1.z.object({
    from: wireDateSchema,
    to: wireDateSchema,
    /**
     * ⚠️ **The days the calendar has already taken**, so the grid can grey them: a band that runs
     * across a public holiday is not somebody taking that day off, and a timeline that draws them
     * identically invites the argument every year.
     */
    holidays: zod_1.z.array(wireDateSchema),
    /**
     * ⚠️ **The same days, with the calendar's own name for each** — an addition, 3 Nov 2026
     * (`decisions.md` DASH-2): the chart flags a holiday under its date and names it on hover.
     * `holidays` stays beside it for the readers that only need the dates. Defaulted, so an
     * answer from before the addition still parses.
     */
    holidayNames: zod_1.z.array(zod_1.z.object({ date: wireDateSchema, name: zod_1.z.string() })).default([]),
    rows: zod_1.z.array(exports.leaveTimelineRowSchema),
});
//# sourceMappingURL=dashboards.js.map